import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Grid,
  IconButton,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '../store/languageStore';
import { translations } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface SubmissionPreviewDialogProps {
  open?: boolean;
  data: Record<string, any> | null;
  onProceed: () => void;
  onEdit: () => void;
  /** When true, renders as an inline panel (no Dialog wrapper) */
  panel?: boolean;
  /** Label for the proceed/submit button */
  proceedLabel?: string;
  /** Label for the edit/back button */
  editLabel?: string;
  /** Hide the proceed button (e.g. view-only panel) */
  hideProceed?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Returns true when a value is non-empty and should be displayed. */
function hasValue(v: any): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
}

/** Safely read a dot-notation path from an object. */
function get(obj: any, path: string): any {
  return path.split('.').reduce((acc, k) => (acc != null ? acc[k] : undefined), obj);
}

/** Format a value for display. Safely handles arrays, booleans, and primitives. */
function formatValue(v: any): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.filter(x => x !== null && x !== undefined).join(', ');
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// ─────────────────────────────────────────────────────────────────────────────
// Field → label map (keyed by bare field name, without "kopf." prefix)
// ─────────────────────────────────────────────────────────────────────────────

const FIELD_LABELS_EN: Record<string, string> = {
  // Registration types
  artderAnmeldung: 'Type of Registration',
  artderAusfuhranmeldung: 'Type of Export Declaration',
  // General information
  datum: 'Date of Departure',
  sicherheit: 'Security',
  massgeblichesDatum: 'Relevant Date',
  containerIndikator: 'Container Indicator',
  eoriNiederlassungsnummer: 'EORI Branch Number',
  lrn: 'Reference Number (LRN)',
  bezugsnummer: 'Shipment Reference Number (UCR)',
  beforderungskostenZahlungsart: 'Transportation Costs (Payment Method)',
  besondereUmstande: 'Indicator of Special Circumstances',
  gesamtRohmasse: 'Total Gross Mass',
  beteiligtenKonstellation: 'Constellation of Parties Involved',
  anzahlPositionen: 'Number of Positions',
  userid: 'User ID',
  dienststellennummer: 'Customs Office Number',
  versionsnr: 'Version Number',
  erstelldatum: 'Creation Date',
  // Provision
  zeitpunktderAnmeldung: 'Time of Presentation',
  zeitpunktdesEndesderLadetatigkeit: 'Time of End of Loading Activity',
  zeitpunktDerBeendigungDerVerladetatigkeit: 'Time of End of Loading Activity',
  zeitpunktDerGestellung: 'Presentation Timestamp',
  // Transport
  inlandischerVerkehrszweig: 'Domestic Transport Sector',
  verkehrszweigAnDerGrenze: 'Transport Route at the Border',
  beforderungsmittelderGrenzeArt: 'Cross-border Active Means of Transport / Type of Identification',
  beforderungsmittelderGrenzeKennzeichen: 'Cross-border Active Means of Transport / Registration',
  beforderungsmittelderGrenzeStaatszugehorigkeit: 'Cross-border Active Means of Transport / Nationality',
  artEinheitsBeforderungsmittels: 'Unit of Transport Means Type',
  // Customs offices
  ausfuhrzollstelleDienststellennummer: 'Export Customs Office',
  ausfuhrZollstelleFurDieErganzende: 'Export Customs Office for the Supplementary/Replacement Declaration',
  erganzungszollstelleAusfuhrDienststellennummer: 'Export Customs Office for the Supplementary/Replacement Declaration',
  vorgeseheneAusgangszollstelleDienststellennummer: 'Intended Customs Office of Exit',
  tatsachlicheAusgangszollstelleDienststellennummer: 'Actual Customs Office of Exit',
  gestellungszollstelle: 'Presentation Customs Office',
  // Business transaction
  artdesGeschafts: 'Type of Business',
  inRechnunggestellterGesamtbetrag: 'Total Amount Invoiced',
  rechnungswahrung: 'Invoice Currency',
  // Delivery terms
  lieferbedingungIncotermCode: 'Incoterms Code',
  lieferbedingungUnLocode: 'UN/Locode',
  lieferbedingungOrt: 'Location',
  lieferbedingungLand: 'Country',
  lieferbedingungText: 'Text',
  lieferbedingungOrtCode: 'Location Code',
  // Countries
  ausfuhrland: 'Export Country',
  bestimmungsLand: 'Destination Country',
  // Location of goods (warenort)
  warenortArtdesOrtes: 'Type of Place',
  warenortArtDerOrtsbestimmung: 'Type of Location Determination',
  warenortBewilligungsnummer: 'Approval Number',
  warenortZusatzlicheKennung: 'Additional Identifier',
  warenortUnLocode: 'UN/Locode',
  warenortGnssBreite: 'GNSS Latitude',
  warenortGnssLange: 'GNSS Length',
  // Location address sub-fields
  warenortZusatzlicheAngaben: 'Additional Information',
  warenortStrasse: 'Street and House Number',
  warenortPlz: 'Postal Code',
  warenortOrt: 'Location',
  warenortLand: 'Country',
  // Participant shared fields
  name: 'Name',
  strasse: 'Street and House Number',
  plz: 'Postal Code',
  ort: 'Location',
  land: 'Country',
  tin: 'Identification Number',
  niederlassungsNummer: 'Branch Number',
  kundennummer: 'Customer Number',
  angezeigterName: 'Displayed Name',
  identifikationsart: 'Identification Type',
  beauftragterStellung: 'Representative Status',
  tinCheck: 'TIN Check',
  // Contact person sub-fields
  ansprechpartnerName: 'Name',
  telefonnummer: 'Telephone Number',
  email: 'E-mail Address',
  ansprechpartnerAngezeigterName: 'Displayed Name',
  ansprechName: 'Name',
  ansprechPhone: 'Telephone Number',
  ansprechEmail: 'E-mail Address',
  beauftragterName: 'Name',
  // Departure transport
  artderIdentifikation: 'Type of Identification',
  kennzeichen: 'Registration',
  staatszugehorigkeit: 'Nationality',
  // Position fields
  warePositionsnummer: 'Position Number',
  wareWarenbezeichnung: 'Product Description',
  wareWarennummerKN8: 'HS/CN Code',
  wareEigenmasse: 'Net Weight (kg)',
  wareRohmasse: 'Gross Weight (kg)',
  menge: 'Quantity',
  mengeinbesondererMabeinheit: 'Quantity in Special Unit',
  beantragtesVerfahren: 'Requested Procedure',
  vorhergehendesVerfahren: 'Previous Procedure',
  ursprungsland: 'Country of Origin',
  statistischerWert: 'Statistical Value',
  artdesGeschaftsPosition: 'Type of Transaction',
  // Other
  ladeort: 'Loading Place',
};

const FIELD_LABELS_DE: Record<string, string> = {
  artderAnmeldung: 'Art der Anmeldung',
  artderAusfuhranmeldung: 'Art der Ausfuhranmeldung',
  datum: 'Abgangsdatum',
  sicherheit: 'Sicherheit',
  massgeblichesDatum: 'Maßgebliches Datum',
  containerIndikator: 'Container-Indikator',
  eoriNiederlassungsnummer: 'EORI-Niederlassungsnummer',
  lrn: 'Referenznummer (LRN)',
  bezugsnummer: 'Sendungsbezugsnummer (UCR)',
  beforderungskostenZahlungsart: 'Beförderungskosten (Zahlungsart)',
  besondereUmstande: 'Indikator für besondere Umstände',
  gesamtRohmasse: 'Gesamtrohmasse',
  beteiligtenKonstellation: 'Beteiligten-Konstellation',
  anzahlPositionen: 'Anzahl der Positionen',
  userid: 'Benutzer-ID',
  dienststellennummer: 'Dienststellennummer',
  versionsnr: 'Versionsnummer',
  erstelldatum: 'Erstelldatum',
  zeitpunktderAnmeldung: 'Zeitpunkt der Gestellung',
  zeitpunktdesEndesderLadetatigkeit: 'Zeitpunkt des Endes der Ladetätigkeit',
  zeitpunktDerBeendigungDerVerladetatigkeit: 'Zeitpunkt des Endes der Ladetätigkeit',
  zeitpunktDerGestellung: 'Zeitpunkt der Gestellung',
  inlandischerVerkehrszweig: 'Inländischer Verkehrszweig',
  verkehrszweigAnDerGrenze: 'Verkehrszweig an der Grenze',
  beforderungsmittelderGrenzeArt: 'Grenzüberschreitendes Beförderungsmittel / Art',
  beforderungsmittelderGrenzeKennzeichen: 'Grenzüberschreitendes Beförderungsmittel / Kennzeichen',
  beforderungsmittelderGrenzeStaatszugehorigkeit: 'Grenzüberschreitendes Beförderungsmittel / Staatszugehörigkeit',
  artEinheitsBeforderungsmittels: 'Art des Einheitsbeförderungsmittels',
  ausfuhrzollstelleDienststellennummer: 'Ausfuhrzollstelle',
  ausfuhrZollstelleFurDieErganzende: 'Ausfuhrzollstelle für die ergänzende/ersetzende Anmeldung',
  erganzungszollstelleAusfuhrDienststellennummer: 'Ausfuhrzollstelle für die ergänzende/ersetzende Anmeldung',
  vorgeseheneAusgangszollstelleDienststellennummer: 'Vorgesehene Ausgangszollstelle',
  tatsachlicheAusgangszollstelleDienststellennummer: 'Tatsächliche Ausgangszollstelle',
  gestellungszollstelle: 'Gestellungszollstelle',
  artdesGeschafts: 'Art des Geschäfts',
  inRechnunggestellterGesamtbetrag: 'In Rechnung gestellter Gesamtbetrag',
  rechnungswahrung: 'Rechnungswährung',
  lieferbedingungIncotermCode: 'Incoterms-Code',
  lieferbedingungUnLocode: 'UN/Locode',
  lieferbedingungOrt: 'Ort',
  lieferbedingungLand: 'Land',
  lieferbedingungText: 'Text',
  lieferbedingungOrtCode: 'Ortscode',
  ausfuhrland: 'Ausfuhrland',
  bestimmungsLand: 'Bestimmungsland',
  warenortArtdesOrtes: 'Art des Ortes',
  warenortArtDerOrtsbestimmung: 'Art der Ortsbestimmung',
  warenortBewilligungsnummer: 'Bewilligungsnummer',
  warenortZusatzlicheKennung: 'Zusätzliche Kennung',
  warenortUnLocode: 'UN/Locode',
  warenortGnssBreite: 'GNSS-Breite',
  warenortGnssLange: 'GNSS-Länge',
  warenortZusatzlicheAngaben: 'Zusätzliche Angaben',
  warenortStrasse: 'Straße und Hausnummer',
  warenortPlz: 'Postleitzahl',
  warenortOrt: 'Ort',
  warenortLand: 'Land',
  name: 'Name',
  strasse: 'Straße und Hausnummer',
  plz: 'Postleitzahl',
  ort: 'Ort',
  land: 'Land',
  tin: 'Identifikationsnummer',
  niederlassungsNummer: 'Niederlassungsnummer',
  kundennummer: 'Kundennummer',
  angezeigterName: 'Angezeigter Name',
  identifikationsart: 'Identifikationsart',
  beauftragterStellung: 'Stellung des Beauftragten',
  tinCheck: 'TIN-Prüfung',
  ansprechpartnerName: 'Name',
  telefonnummer: 'Telefonnummer',
  email: 'E-Mail-Adresse',
  ansprechpartnerAngezeigterName: 'Angezeigter Name',
  ansprechName: 'Name',
  ansprechPhone: 'Telefonnummer',
  ansprechEmail: 'E-Mail-Adresse',
  beauftragterName: 'Name',
  artderIdentifikation: 'Art der Identifikation',
  kennzeichen: 'Kennzeichen',
  staatszugehorigkeit: 'Staatszugehörigkeit',
  warePositionsnummer: 'Positionsnummer',
  wareWarenbezeichnung: 'Warenbezeichnung',
  wareWarennummerKN8: 'Warennummer KN8',
  wareEigenmasse: 'Eigenmasse (kg)',
  wareRohmasse: 'Rohmasse (kg)',
  menge: 'Menge',
  mengeinbesondererMabeinheit: 'Menge in besonderer Maßeinheit',
  beantragtesVerfahren: 'Beantragtes Verfahren',
  vorhergehendesVerfahren: 'Vorhergehendes Verfahren',
  ursprungsland: 'Ursprungsland',
  statistischerWert: 'Statistischer Wert',
  artdesGeschaftsPosition: 'Art des Geschäfts',
  ladeort: 'Ladeort',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-section field configuration
// ─────────────────────────────────────────────────────────────────────────────

const REGISTRATION_TYPE_FIELDS = ['artderAnmeldung', 'artderAusfuhranmeldung'];

const GENERAL_INFO_FIELDS = [
  'datum', 'sicherheit', 'massgeblichesDatum', 'containerIndikator',
  'eoriNiederlassungsnummer', 'lrn', 'bezugsnummer',
  'beforderungskostenZahlungsart', 'besondereUmstande',
  'gesamtRohmasse', 'beteiligtenKonstellation',
];

const PROVISION_FIELDS = [
  'zeitpunktderAnmeldung', 'zeitpunktdesEndesderLadetatigkeit',
  'zeitpunktDerBeendigungDerVerladetatigkeit', 'zeitpunktDerGestellung',
];

const TRANSPORT_MEANS_FIELDS = [
  'inlandischerVerkehrszweig', 'verkehrszweigAnDerGrenze',
  'beforderungsmittelderGrenzeArt', 'beforderungsmittelderGrenzeKennzeichen',
  'beforderungsmittelderGrenzeStaatszugehorigkeit', 'artEinheitsBeforderungsmittels',
];

const CUSTOMS_OFFICE_FIELDS = [
  'ausfuhrzollstelleDienststellennummer', 'ausfuhrZollstelleFurDieErganzende',
  'erganzungszollstelleAusfuhrDienststellennummer',
  'vorgeseheneAusgangszollstelleDienststellennummer',
  'tatsachlicheAusgangszollstelleDienststellennummer', 'gestellungszollstelle',
];

const BUSINESS_TRANSACTION_FIELDS = [
  'artdesGeschafts', 'inRechnunggestellterGesamtbetrag', 'rechnungswahrung',
];

const DELIVERY_TERMS_FIELDS = [
  'lieferbedingungIncotermCode', 'lieferbedingungUnLocode',
  'lieferbedingungOrt', 'lieferbedingungLand', 'lieferbedingungText', 'lieferbedingungOrtCode',
];

const GENERAL_INFO_FOOTER_FIELDS = ['anzahlPositionen', 'userid', 'dienststellennummer', 'versionsnr', 'erstelldatum'];

// All "known" kopf-level scalar/simple fields — used to identify "other" fields
const ALL_CATEGORIZED_KOPF_FIELDS = new Set([
  ...REGISTRATION_TYPE_FIELDS,
  ...GENERAL_INFO_FIELDS,
  ...PROVISION_FIELDS,
  ...TRANSPORT_MEANS_FIELDS,
  ...CUSTOMS_OFFICE_FIELDS,
  ...BUSINESS_TRANSACTION_FIELDS,
  ...DELIVERY_TERMS_FIELDS,
  ...GENERAL_INFO_FOOTER_FIELDS,
  // Participants (object keys)
  'empfanger', 'anmelder', 'vertreter', 'subunternehmer',
  'ausfuhrer', 'aussenAusfuhrer', 'beforderer', 'versender',
  // Transport arrays
  'beforderungsmittelBeimAbgang', 'beforderungsroute',
  // Warenort
  'warenortArtdesOrtes', 'warenortArtDerOrtsbestimmung', 'warenortBewilligungsnummer',
  'warenortZusatzlicheKennung', 'warenortUnLocode', 'warenortGnssBreite', 'warenortGnssLange',
  'warenortZusatzlicheAngaben', 'warenortStrasse', 'warenortPlz', 'warenortOrt', 'warenortLand',
  'warenortAnsprechpartnerName', 'warenortTelefonnummer', 'warenortEmail',
  // Misc system fields shown elsewhere
  'bewilligung', 'ladeort', 'ausfuhrland', 'bestimmungsLand',
  // File metadata keys that may be attached
  'file_ids', 'file_names', 'transaction_ids',
]);

const PARTICIPANT_KEYS = [
  { key: 'empfanger', labelKey: 'submissionPreview.participants.empfanger' },
  { key: 'anmelder',  labelKey: 'submissionPreview.participants.anmelder' },
  { key: 'vertreter', labelKey: 'submissionPreview.participants.vertreter' },
  { key: 'subunternehmer', labelKey: 'submissionPreview.participants.subunternehmer' },
  { key: 'ausfuhrer', labelKey: 'submissionPreview.participants.ausfuhrer' },
  { key: 'aussenAusfuhrer', labelKey: 'submissionPreview.participants.aussenAusfuhrer' },
  { key: 'beforderer', labelKey: 'submissionPreview.participants.beforderer' },
  { key: 'versender',  labelKey: 'submissionPreview.participants.versender' },
];

const CONTACT_PERSON_FIELDS = [
  'ansprechpartnerName', 'telefonnummer', 'email', 'ansprechpartnerAngezeigterName',
  'ansprechName', 'ansprechPhone', 'ansprechEmail', 'beauftragterName',
];

const WARENORT_MAIN_FIELDS = [
  'warenortArtdesOrtes', 'warenortArtDerOrtsbestimmung', 'warenortBewilligungsnummer',
  'warenortZusatzlicheKennung', 'warenortUnLocode', 'warenortGnssBreite', 'warenortGnssLange',
];

const WARENORT_ADDRESS_FIELDS = [
  'warenortZusatzlicheAngaben', 'warenortStrasse', 'warenortPlz', 'warenortOrt', 'warenortLand',
];

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface SectionCardProps { label: string; children: React.ReactNode; fullWidth?: boolean }
const SectionCard: React.FC<SectionCardProps> = ({ label, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box
        sx={{
          px: 1,
          py: 0.35,
          bgcolor: `${theme.palette.primary.main}12`,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: theme.palette.primary.main,
            fontSize: '0.65rem',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ p: 0.75 }}>
        {children}
      </Box>
    </Box>
  );
};

interface KVRowProps { label: string; value: string }
// A long label must not squeeze the value out of existence.
//
// The label used to carry flexShrink:0 with whiteSpace:'nowrap', so
// "Cross-border Active Means of Transport / Type of Identification" claimed the
// whole row, left the value a few pixels, and overflowWrap:'anywhere' then
// broke "NORDIC AURORA" at EVERY CHARACTER — one letter per line down the page.
// The same nowrap label overflowed its grid cell and printed on top of the
// neighbouring one.
//
// Now the row wraps: a label too long for the line drops the value beneath it
// instead of crushing it, and the value keeps a floor width so it can never be
// reduced to a single-character column. Breaking is at word boundaries, so a
// value only ever splits between words unless one word genuinely exceeds the
// line.
const KVRow: React.FC<KVRowProps> = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      columnGap: 0.5,
      alignItems: 'baseline',
      py: 0.1,
      minWidth: 0,
    }}
  >
    <Typography
      component="span"
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 500,
        fontSize: '0.67rem',
        lineHeight: 1.4,
        // may wrap, may shrink — never overflows onto the next column
        minWidth: 0,
        overflowWrap: 'break-word',
      }}
    >
      {label}:
    </Typography>
    <Typography
      component="span"
      variant="caption"
      sx={{
        fontWeight: 600,
        fontSize: '0.74rem',
        lineHeight: 1.4,
        flex: '1 1 auto',
        minWidth: '5rem',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {value}
    </Typography>
  </Box>
);

interface ParticipantCardProps {
  title: string;
  data: Record<string, any>;
  fieldLabels: Record<string, string>;
  contactLabel: string;
}
const ParticipantCard: React.FC<ParticipantCardProps> = ({ title, data, fieldLabels, contactLabel }) => {
  const theme = useTheme();

  const mainFields = Object.entries(data).filter(
    ([k, v]) => !CONTACT_PERSON_FIELDS.includes(k) && hasValue(v) && typeof v !== 'object',
  );
  const contactFields = CONTACT_PERSON_FIELDS.filter(k => hasValue(data[k]));

  if (mainFields.length === 0 && contactFields.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        height: '100%',
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          display: 'block',
          mb: 0.5,
          color: theme.palette.primary.main,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          fontSize: '0.67rem',
        }}
      >
        {title}
      </Typography>

      <Grid container spacing={0} sx={{ mt: 0 }}>
        {mainFields.map(([k, v]) => (
          <Grid item xs={12} key={k}>
            <KVRow label={fieldLabels[k] || k} value={formatValue(v)} />
          </Grid>
        ))}
      </Grid>

      {contactFields.length > 0 && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, mb: 0.25, fontWeight: 700, fontSize: '0.65rem' }}
          >
            {contactLabel}
          </Typography>
          <Grid container spacing={0}>
            {contactFields.map(k => (
              <Grid item xs={12} key={k}>
                <KVRow label={fieldLabels[k] || k} value={formatValue(data[k])} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Paper>
  );
};

interface PositionSectionProps {
  positions: any[];
  fieldLabels: Record<string, string>;
  sectionLabel: string;
  positionLabel: string;
}
const PositionSection: React.FC<PositionSectionProps> = ({
  positions, fieldLabels, sectionLabel, positionLabel,
}) => {
  const theme = useTheme();
  const filled = positions.filter(p => p && typeof p === 'object' && Object.keys(p).length > 0);
  if (filled.length === 0) return null;

  return (
    <SectionCard label={sectionLabel}>
      <Grid container spacing={0.5} sx={{ mt: 0 }}>
        {filled.map((pos, idx) => {
          const entries = Object.entries(pos).filter(([, v]) => hasValue(v) && typeof v !== 'object');
          if (entries.length === 0) return null;
          return (
            <Grid item xs={12} md={6} key={idx}>
              <Paper
                variant="outlined"
                sx={{ p: 1, borderColor: 'divider', borderRadius: 1.5 }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    color: theme.palette.primary.main,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.67rem',
                  }}
                >
                  {positionLabel} {idx + 1}
                </Typography>
                <Grid container spacing={0}>
                  {entries.map(([k, v]) => (
                    <Grid item xs={12} sm={6} key={k}>
                      <KVRow label={fieldLabels[k] || k} value={formatValue(v as any)} />
                    </Grid>
                  ))}
                </Grid>
                {/* Nested objects inside position (e.g. verpackung, unterlage) */}
                {Object.entries(pos)
                  .filter(([, v]) => v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0)
                  .map(([groupKey, groupVal]: [string, any]) => (
                    <Box key={groupKey} sx={{ mt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.25, fontSize: '0.65rem' }}>
                        {fieldLabels[groupKey] || groupKey}
                      </Typography>
                      <Grid container spacing={0}>
                        {Object.entries(groupVal)
                          .filter(([, v]) => hasValue(v) && typeof v !== 'object')
                          .map(([k, v]) => (
                            <Grid item xs={12} sm={6} key={k}>
                              <KVRow label={fieldLabels[k] || k} value={formatValue(v as any)} />
                            </Grid>
                          ))}
                      </Grid>
                    </Box>
                  ))}
                {/* Arrays inside position (e.g. verpackung[], unterlage[]) */}
                {Object.entries(pos)
                  .filter(([, v]) => Array.isArray(v) && (v as any[]).length > 0)
                  .map(([groupKey, groupArr]: [string, any]) => (
                    <Box key={groupKey} sx={{ mt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.25, fontSize: '0.65rem' }}>
                        {fieldLabels[groupKey] || groupKey}
                      </Typography>
                      {(groupArr as any[]).map((item: any, i: number) =>
                        typeof item === 'object' ? (
                          <Grid container spacing={0} key={i} sx={{ pl: 0.5, mb: 0.25 }}>
                            {Object.entries(item)
                              .filter(([, v]) => hasValue(v) && typeof v !== 'object')
                              .map(([k, v]) => (
                                <Grid item xs={12} sm={6} key={k}>
                                  <KVRow label={fieldLabels[k] || k} value={formatValue(v as any)} />
                                </Grid>
                              ))}
                          </Grid>
                        ) : (
                          <Box key={i} sx={{ pl: 0.5 }}>
                            <Typography variant="caption" fontWeight={600}>{formatValue(item)}</Typography>
                          </Box>
                        ),
                      )}
                    </Box>
                  ))}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </SectionCard>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

const SubmissionPreviewDialog: React.FC<SubmissionPreviewDialogProps> = ({
  open = false, data, onProceed, onEdit, panel = false, proceedLabel, editLabel, hideProceed = false,
}) => {
  const { tString, currentLanguage } = useTranslation();
  const fieldLabels = currentLanguage === 'de' ? FIELD_LABELS_DE : FIELD_LABELS_EN;

  const kopf: Record<string, any> = useMemo(() => data?.kopf ?? {}, [data]);
  const positions: any[] = useMemo(() => {
    const raw = data?.position ?? data?.positionen ?? [];
    return Array.isArray(raw) ? raw : [raw];
  }, [data]);

  // Helper: render a list of scalar kopf field names — 2-3 per row inside a SectionCard
  // singleColumn: force one field per row (xs=12). Use for sections whose labels are
  // long enough to break the 2-column layout (e.g. customs offices / Dienststellen).
  const renderKopfFields = (fields: string[], singleColumn = false) => {
    const filled = fields.filter(f => hasValue(kopf[f]));
    if (filled.length === 0) return null;
    return (
      <Grid container spacing={0}>
        {filled.map(f => (
          <Grid item xs={12} sm={singleColumn ? 12 : 6} key={f}>
            <KVRow label={fieldLabels[f] || f} value={formatValue(kopf[f])} />
          </Grid>
        ))}
      </Grid>
    );
  };

  // Departure transport fields (beforderungsmittelBeimAbgang[0])
  const departureTransport: Record<string, any> = useMemo(() => {
    const arr = kopf.beforderungsmittelBeimAbgang;
    return Array.isArray(arr) && arr.length > 0 ? arr[0] : (arr ?? {});
  }, [kopf]);

  // Transit countries — may be stored as a comma-separated string OR a proper array
  const transitCountries: string[] = useMemo(() => {
    const route = kopf.beforderungsroute;
    const val = route?.ausgewahlteLander;
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === 'string') return val.split(',').map((s: string) => s.trim()).filter(Boolean);
    return [];
  }, [kopf]);

  // Compute "other" kopf fields not covered by any sub-section
  const otherKopfFields = useMemo(() => {
    return Object.entries(kopf).filter(([k, v]) => {
      if (!hasValue(v)) return false;
      if (ALL_CATEGORIZED_KOPF_FIELDS.has(k)) return false;
      if (typeof v === 'object') return false; // skip nested objects here
      return true;
    });
  }, [kopf]);

  // Whether any left-column section has data
  const hasDepartureTransportData = Object.values(departureTransport).some(v => hasValue(v));
  const hasTransitCountries = transitCountries.length > 0;
  const hasTransportSection =
    TRANSPORT_MEANS_FIELDS.some(f => hasValue(kopf[f])) ||
    hasDepartureTransportData || hasTransitCountries;

  const hasParticipants = PARTICIPANT_KEYS.some(({ key }) => hasValue(kopf[key]));

  const hasWarenortMain = WARENORT_MAIN_FIELDS.some(f => hasValue(kopf[f]));
  const hasWarenortAddress = WARENORT_ADDRESS_FIELDS.some(f => hasValue(kopf[f]));

  const contactLabel = tString('submissionPreview.sections.contactPerson');

  const hasSection = (fields: string[]) => fields.some(f => hasValue(kopf[f]));

  // ── Shared inner content (used by both panel and dialog modes) ──
  const innerContent = data ? (
    <Grid container spacing={0.75}>

            {/* ── GENERAL INFORMATION — full width, 4-col fields ── */}
            {(hasSection(GENERAL_INFO_FIELDS) || hasSection(GENERAL_INFO_FOOTER_FIELDS)) && (
              <Grid item xs={12}>
                <SectionCard label={tString('submissionPreview.sections.generalInformation')}>
                  <Grid container spacing={0}>
                    {[...GENERAL_INFO_FIELDS, ...GENERAL_INFO_FOOTER_FIELDS]
                      .filter(f => hasValue(kopf[f]))
                      .map(f => (
                        <Grid item xs={6} sm={4} md={3} key={f}>
                          <KVRow label={fieldLabels[f] || f} value={formatValue(kopf[f])} />
                        </Grid>
                      ))}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* ── REGISTRATION TYPES ── */}
            {hasSection(REGISTRATION_TYPE_FIELDS) && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.registrationTypes')}>
                  {renderKopfFields(REGISTRATION_TYPE_FIELDS)}
                </SectionCard>
              </Grid>
            )}

            {/* ── PROVISION INFORMATION ── */}
            {hasSection(PROVISION_FIELDS) && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.provisionInformation')}>
                  {renderKopfFields(PROVISION_FIELDS)}
                </SectionCard>
              </Grid>
            )}

            {/* ── TRANSPORT ── */}
            {hasTransportSection && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.transportMeansDetails')}>
                  <Grid container spacing={0}>
                    {TRANSPORT_MEANS_FIELDS.filter(f => hasValue(kopf[f])).map(f => (
                      <Grid item xs={12} sm={6} key={f}>
                        <KVRow label={fieldLabels[f] || f} value={formatValue(kopf[f])} />
                      </Grid>
                    ))}
                    {hasDepartureTransportData && Object.entries(departureTransport)
                      .filter(([, v]) => hasValue(v) && typeof v !== 'object')
                      .map(([k, v]) => (
                        <Grid item xs={12} sm={6} key={`dep_${k}`}>
                          <KVRow label={fieldLabels[k] || k} value={formatValue(v)} />
                        </Grid>
                      ))}
                    {hasTransitCountries && (
                      <Grid item xs={12} sm={6}>
                        <KVRow
                          label={currentLanguage === 'de' ? 'Transit-Länder' : 'Transit Countries'}
                          value={transitCountries.join(', ')}
                        />
                      </Grid>
                    )}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* ── CUSTOMS OFFICES ── */}
            {hasSection(CUSTOMS_OFFICE_FIELDS) && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.customsOffices')}>
                  {renderKopfFields(CUSTOMS_OFFICE_FIELDS, true)}
                </SectionCard>
              </Grid>
            )}

            {/* ── BUSINESS TRANSACTION ── */}
            {hasSection(BUSINESS_TRANSACTION_FIELDS) && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.businessTransaction')}>
                  {renderKopfFields(BUSINESS_TRANSACTION_FIELDS)}
                </SectionCard>
              </Grid>
            )}

            {/* ── DELIVERY TERMS ── */}
            {hasSection(DELIVERY_TERMS_FIELDS) && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.deliveryTerms')}>
                  {renderKopfFields(DELIVERY_TERMS_FIELDS)}
                </SectionCard>
              </Grid>
            )}

            {/* ── LOCATION OF GOODS ── */}
            {hasWarenortMain && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.locationOfGoods')}>
                  {renderKopfFields(WARENORT_MAIN_FIELDS)}
                </SectionCard>
              </Grid>
            )}

            {/* ── LOCATION ADDRESS ── */}
            {hasWarenortAddress && (
              <Grid item xs={12} md={6}>
                <SectionCard label={tString('submissionPreview.sections.locationAddress')}>
                  <Grid container spacing={0}>
                    {WARENORT_ADDRESS_FIELDS.filter(f => hasValue(kopf[f])).map(f => (
                      <Grid item xs={12} sm={6} key={f}>
                        <KVRow label={fieldLabels[f] || f} value={formatValue(kopf[f])} />
                      </Grid>
                    ))}
                    {['warenortAnsprechpartnerName', 'warenortTelefonnummer', 'warenortEmail']
                      .filter(f => hasValue(kopf[f]))
                      .map(f => (
                        <Grid item xs={12} sm={6} key={f}>
                          <KVRow label={fieldLabels[f] || f} value={formatValue(kopf[f])} />
                        </Grid>
                      ))}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* ── OTHER UNCATEGORISED FIELDS ── */}
            {otherKopfFields.length > 0 && (
              <Grid item xs={12}>
                <SectionCard label={tString('submissionPreview.sections.otherFields')}>
                  <Grid container spacing={0}>
                    {otherKopfFields.map(([k, v]) => (
                      <Grid item xs={6} sm={4} md={3} key={k}>
                        <KVRow label={fieldLabels[k] || k} value={formatValue(v)} />
                      </Grid>
                    ))}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* ── PARTICIPANTS — full width, cards in 4-col grid ── */}
            {hasParticipants && (
              <Grid item xs={12}>
                <SectionCard label={tString('submissionPreview.sections.participants')}>
                  <Grid container spacing={0.5}>
                    {PARTICIPANT_KEYS.map(({ key, labelKey }) => {
                      const pData = kopf[key];
                      if (!pData || typeof pData !== 'object') return null;
                      return (
                        <Grid item xs={12} sm={6} md={3} key={key}>
                          <ParticipantCard
                            title={tString(labelKey)}
                            data={pData}
                            fieldLabels={fieldLabels}
                            contactLabel={contactLabel}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </SectionCard>
              </Grid>
            )}

            {/* ── POSITIONS — full width ── */}
            {positions.length > 0 && (
              <Grid item xs={12}>
                <PositionSection
                  positions={positions}
                  fieldLabels={fieldLabels}
                  sectionLabel={tString('submissionPreview.sections.positions')}
                  positionLabel={currentLanguage === 'de' ? 'Position' : 'Position'}
                />
              </Grid>
            )}

          </Grid>
  ) : null;

  // ── Shared footer buttons ──
  const footerButtons = (
    <>
      <Button variant="outlined" color="inherit" onClick={onEdit}>
        {editLabel ?? tString('submissionPreview.edit')}
      </Button>
      {!hideProceed && (
        <Button variant="contained" color="primary" onClick={onProceed}>
          {proceedLabel ?? tString('submissionPreview.proceed')}
        </Button>
      )}
    </>
  );

  // ── Panel mode: inline, no Dialog wrapper ──
  if (panel) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          {innerContent}
        </Box>
        {/* Pinned footer */}
        <Box
          sx={{
            px: 2, py: 1.25, pr: 10,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
          }}
        >
          {footerButtons}
        </Box>
      </Box>
    );
  }

  // ── Dialog mode (default) ──
  return (
    <Dialog
      open={open}
      onClose={onEdit}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      aria-labelledby="preview-dialog-title"
    >
      <DialogTitle
        id="preview-dialog-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          {tString('submissionPreview.title')}
        </Typography>
        <IconButton onClick={onEdit} size="small" aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 1.5 }}>
        {innerContent}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2, py: 1.25,
          borderTop: 1,
          borderColor: 'divider',
          gap: 1,
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
        }}
      >
        {footerButtons}
      </DialogActions>
    </Dialog>
  );
};

export default SubmissionPreviewDialog;
